import type {ReactNode} from 'react';
import {ErrorBoundary as ReactErrorBoundary} from 'react-error-boundary';
import type {AssistantMessageRowProps} from './assistant-message-row.types';
import styles from './assistant-message-row.module.scss';
import MarkdownRenderer from '@Components/ai-chat-window/components/markdown-renderer/markdown-renderer';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {GeneratedImage} from '@Components/ai-chat-window/components/generated-image/generated-image';
import {getUniqueKeyForImagePart, getUrlForGeneratedAiChatModelAssetsImage} from '@Libraries/ai-chat.library';
import type {GeneratedImagePartActionsProps} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {MAX_CHAT_IMAGE_DIMENSION} from '@Components/ai-chat-window/components/generated-image/generated-image.types';
import {AssistantMessageImagePartActionsRow} from '@Components/ai-chat-window/components/assistant-message-image-part-actions-row/assistant-message-image-part-actions-row';
import {SuperUserImageContextDetails} from './components/super-user-image-context-details/super-user-image-context-details';
import {AssistantMessageImagePartActionTiles} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles';
import {AssistantMessageImagePartAnimationCreations} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations';
import type {ChatMessageModelImagePart, ChatMessagePart, ChatMessageTextPart} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMessagePartType} from '@Components/ai-chat-window/ai-chat-window.types';
import {renderStreamComponent} from '@Components/ai-chat-window/lib/stream-component-registry';
import {SkeletonBrickTheme} from '@Components/skeleton-brick';
import {getPosterHashedIdForAiChatImageAsset} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {openSharingModal} from '@Modals/share-modal/share-modal-library';
import {SHARE_PANEL_ID, ShareItemType} from '@Panels/share-panel/share-panel.types';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {LoadingStates} from '@Utils/loading.util';
import {GA4EventName} from '@Libraries/ga-events';
import {AiThreeDotsLoader} from '@Components/ai-chat-window/components/ai-three-dots-loader/ai-three-dots-loader';
import {closeAiActionLoadingModal, isCurrentModalSession, openShareLoadingModalAndGetSessionId} from '@Components/ai-chat-window/lib/ai-action-loading-modal.library';
import {useSelector} from 'react-redux';
import type {RootState} from '@/store';
import {CyclingLoadingText} from '@Components/ai-chat-window/components/cycling-loading-text/cycling-loading-text';
import {PmwLogo} from '@Components/ai-chat-window/components/pmw-logo/pmw-logo';
import {useOpenChatImageInEditor} from '@Components/ai-chat-window/hooks/useOpenChatImageInEditor';
import {openAiChatImageDownloadModal} from '@Components/ai-chat-window/lib/ai-chat-downloads.library';

function isModelGeneratedImagePart(part: ChatMessagePart): part is ChatMessageModelImagePart {
  return part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE;
}

/**
 * Stream-component payloads are LLM-authored, so one malformed card must not
 * white-screen the whole chat — the boundary drops just that card and logs.
 */
function onStreamComponentRenderError(error: Error): void {
  console.error(`Stream component render failed: ${error.message}`);
}

export function AssistantMessageRow({...props}: AssistantMessageRowProps): ReactNode {
  const {state, actions} = useAiChat();
  const {openPosterInEditor} = useOpenChatImageInEditor();

  const {hasWriteAccess, useSmartAssetSelection} = useSelector((state: RootState) => {
    const chatState = state.aiChatWindow[props.chatId];
    return {
      hasWriteAccess: chatState?.hasWriteAccess ?? true,
      useSmartAssetSelection: chatState?.useSmartAssetSelection ?? null,
    };
  });

  const getImagePart = (part: ChatMessageModelImagePart): ReactNode => {
    const getImageSrc = (): string => {
      if (part.isSkeletonPlaceholder) {
        return '';
      }

      if (part.base64ImageData) {
        return `data:image/png;base64,${part.base64ImageData}`;
      }

      return getUrlForGeneratedAiChatModelAssetsImage(part.hashedId, MediaItemSizeType.HIGH_RES);
    };

    const handleImageClick = (): void => {
      props.onImageClick(props.message.messageId, getUniqueKeyForImagePart(part));
    };

    const getSkeltonLoaderTheme = (): SkeletonBrickTheme | undefined => {
      if (part.isSkeletonPlaceholder) {
        return SkeletonBrickTheme.AI_ROTATING_GRADIENT;
      }

      if (part.isNewGeneration) {
        return SkeletonBrickTheme.AI_ROTATING_GRADIENT;
      }

      return undefined;
    };

    const isWaitingForImageGeneratedEvent = !part.isSkeletonPlaceholder && props.isLastMessage && props.isMessageStreaming;

    return (
      <>
        <GeneratedImage
          messages={props.messages}
          message={props.message}
          isLastMessage={props.isLastMessage}
          onImageClick={part.isSkeletonPlaceholder ? undefined : handleImageClick}
          onReattach={
            !part.hashedId
              ? undefined
              : (): void => {
                  props.onReattach(part, getImageSrc());
                }
          }
          isMobile={props.isMobile}
          showFirstImageGenerationDelight={part.isSkeletonPlaceholder ? false : part.showFirstImageGenerationDelight}
          skeltonLoaderTheme={getSkeltonLoaderTheme()}
          width={part.width ?? MAX_CHAT_IMAGE_DIMENSION}
          height={part.height ?? MAX_CHAT_IMAGE_DIMENSION}
          src={getImageSrc()}
          className={`radius-8`}
          alt={part.isSkeletonPlaceholder ? '' : window.i18next.t('pmwjs_ai_generated_content')}
          hashedId={part.isSkeletonPlaceholder ? '' : part.hashedId}
          chatId={props.chatId}
          generatedImagePartActionsProps={getGeneratedImagePartActionsProps(part.isSkeletonPlaceholder ? '' : part.hashedId)}
          isWaitingForImageGeneratedEvent={isWaitingForImageGeneratedEvent}
          isMessageStreaming={props.isMessageStreaming}
          isSkeletonOnly={part.isSkeletonPlaceholder}
        />
        <AssistantMessageImagePartActionsRow
          isMobile={props.isMobile}
          imageWidth={part.width ?? undefined}
          imageHeight={part.height ?? undefined}
          isLastMessage={props.isLastMessage}
          isMessageStreaming={props.isMessageStreaming}
          chatId={props.chatId}
          hashedId={part.hashedId}
          {...getGeneratedImagePartActionsProps(part.hashedId)}
        />
        <AssistantMessageImagePartActionTiles
          chatId={props.chatId}
          hashedId={part.hashedId}
          isFirstImageGeneration={props.isFirstImageGeneration}
          isLatestCreateMessage={props.isLatestCreateMessage}
          isLastMessage={props.isLastMessage}
          isMessageStreaming={props.isMessageStreaming}
          hasExistingAnimations={(part.animations?.length ?? 0) > 0}
        />
        <AssistantMessageImagePartAnimationCreations
          chatId={props.chatId}
          hashedId={part.hashedId}
          animations={part.animations}
          imageWidth={part.width}
          imageHeight={part.height}
        />
      </>
    );
  };

  const showLogo = props.isLastMessage;

  const getTextPart = (part: ChatMessageTextPart, showLoader: boolean): ReactNode => {
    const isPureLoadingState = showLoader && (part.loadingTexts?.length ?? 0) > 0;

    if (isPureLoadingState) {
      return (
        <div className={styles.loadingRow}>
          {showLogo ? <PmwLogo className={styles.inlineLogo} animated /> : null}
          <CyclingLoadingText loadingTexts={part.loadingTexts ?? []} />
        </div>
      );
    }

    if (part.text) {
      return <MarkdownRenderer content={part.text} suffix={showLoader ? <AiThreeDotsLoader className={'spacing-m-l-1'} /> : undefined} />;
    }

    return (
      <div className={styles.loadingRow}>
        {showLogo ? <PmwLogo className={styles.inlineLogo} animated /> : null}
        <AiThreeDotsLoader />
      </div>
    );
  };

  const onDownload = (hashedId: string): void => {
    if (!hashedId) {
      return;
    }

    openAiChatImageDownloadModal(hashedId, props.chatId);
  };

  const onShareDesign = async (hashedId: string): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_SHARE);
    actions.updateLoadingStatesForImageActions(props.chatId, hashedId, {shareLoadingState: LoadingStates.LOADING});
    const sessionId = openShareLoadingModalAndGetSessionId();

    try {
      const posterHashedId = await getPosterHashedIdForAiChatImageAsset(hashedId);

      if (!isCurrentModalSession(sessionId)) {
        return;
      }

      openSharingModal({
        itemHashedId: posterHashedId,
        type: ShareItemType.POSTER,
        panelId: SHARE_PANEL_ID,
      });
    } catch (e: unknown) {
      console.error(e);
      actions.updateLoadingStatesForImageActions(props.chatId, hashedId, {shareLoadingState: LoadingStates.NOT_LOADED});
    } finally {
      actions.updateLoadingStatesForImageActions(props.chatId, hashedId, {shareLoadingState: LoadingStates.LOADED});
      closeAiActionLoadingModal();
    }
  };

  const getGeneratedImagePartActionsProps = (hashedId: string): GeneratedImagePartActionsProps => {
    return {
      onDownload: onDownload.bind(null, hashedId),
      onOpenPosterInEditor: openPosterInEditor.bind(null, props.chatId, hashedId),
      onShareDesign: onShareDesign.bind(null, hashedId),
      canWrite: hasWriteAccess,
      onRegenerate: props.onRegenerate,
      onGenerate: props.onGenerate,
    };
  };

  const getSuperUserContextDetails = (): ReactNode => {
    if (!state.isSuperUser || state.hideGenerationContext) {
      return null;
    }

    const getImageProviderFromParts = (): string | null => {
      const imagePart = (props.message.parts ?? []).find(isModelGeneratedImagePart);
      return imagePart?.provider ?? null;
    };

    const getImageModelFromParts = (): string | null => {
      const imagePart = (props.message.parts ?? []).find(isModelGeneratedImagePart);
      return imagePart?.model ?? null;
    };

    return <SuperUserImageContextDetails enhancedPrompt={props.message.enhancedPrompt} context={props.message.context} useSmartAssetSelection={useSmartAssetSelection} producedByAgent={props.message.producedByAgent} imageProvider={getImageProviderFromParts()} imageModel={getImageModelFromParts()} />;
  };

  const getTrailingLogo = (canShow: boolean): ReactNode => {
    if (!showLogo || !canShow) {
      return null;
    }

    return <PmwLogo wrapperClassName={styles.trailingLogo} animated={props.showLoader === true} />;
  };

  const getAssistantMessage = (): ReactNode => {
    const rawParts = props.message.parts ?? [];
    const nonComponentParts = rawParts.filter((part) => part.type !== ChatMessagePartType.COMPONENT);
    const componentParts = rawParts.filter((part) => part.type === ChatMessagePartType.COMPONENT);
    const parts = [...nonComponentParts, ...componentParts];
    const textParts = parts.filter((part) => part.type === ChatMessagePartType.TEXT);
    const lastTextPartIndex = textParts.length > 0 ? parts.findIndex((part) => part === textParts[textParts.length - 1]) : -1;
    const hasRenderedContent = parts.some((part) => (part.type === ChatMessagePartType.TEXT && Boolean(part.text)) || part.type === ChatMessagePartType.MODEL_GENERATED_IMAGE);
    // Mirror the loading branches in getTextPart: a text part renders its own inline logo when it
    // shows a loading row — either the last part streaming loading texts, or any empty text part.
    // The trailing logo must not also render then, or the streaming message shows two logos.
    const hasInlineLoadingLogo = parts.some((part, index) => {
      if (part.type !== ChatMessagePartType.TEXT) {
        return false;
      }

      const isPureLoadingState = index === lastTextPartIndex && props.showLoader === true && (part.loadingTexts?.length ?? 0) > 0;

      return isPureLoadingState || !part.text;
    });

    return (
      <div className={`_full-width ${styles.assistantMessage}`}>
        {parts.map((part, index) => {
          switch (part.type) {
            case ChatMessagePartType.TEXT: {
              const isLastTextPart = index === lastTextPartIndex;

              return (
                <div
                  key={`model-part-${index}`}
                  className={`${!part.text ? props.bubbleClassNames : ''} ${styles.assistantText} ${props.message.isThinking ? styles.thinkingText : ''}`}
                >
                  {getTextPart(part, isLastTextPart && props.showLoader === true)}
                </div>
              );
            }
            case ChatMessagePartType.MODEL_GENERATED_IMAGE: {
              return (
                <div key={`model-part-${index}`} className="flex-v-row gap-3 spacing-m-b-3 spacing-m-t-3">
                  {getImagePart(part)}
                </div>
              );
            }
            case ChatMessagePartType.COMPONENT: {
              const rendered = renderStreamComponent(part, {
                chatId: props.chatId,
                onResumeWorkflow: props.onResumeWorkflow,
              });
              if (!rendered) {
                return null;
              }
              return (
                <div key={`model-part-${index}`} className="spacing-m-t-1">
                  <ReactErrorBoundary fallback={<></>} onError={onStreamComponentRenderError}>
                    {rendered}
                  </ReactErrorBoundary>
                </div>
              );
            }
            default:
              return null;
          }
        })}
        {getTrailingLogo(hasRenderedContent && !hasInlineLoadingLogo)}
        {getSuperUserContextDetails()}
      </div>
    );
  };

  return getAssistantMessage();
}
